/**
 * CraveTheory header — drawer + Glaze Seal sticky rail
 * Sticky is a fixed overlay (not in-flow), so the default header never shifts.
 */
import {
  getScrollContainer,
  getScrollTop,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const SELECTOR = '[data-ct-header]';

class CtHeader {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.drawer = root.querySelector('[data-ct-drawer]');
    this.overlay = root.querySelector('[data-ct-drawer-overlay]');
    this.openBtns = root.querySelectorAll('[data-ct-drawer-open]');
    this.closeBtns = root.querySelectorAll('[data-ct-drawer-close]');
    this.panel = root.querySelector('[data-ct-drawer-panel]');
    this.bar = root.querySelector('[data-ct-header-bar]') || root.querySelector('.ct-header__bar');
    this.rail = root.querySelector('[data-ct-sticky-rail]');
    this.focusReturn = null;

    this.stickyEnabled = root.dataset.stickyEnabled !== 'false';
    this.stickyThreshold = Number(root.dataset.stickyThreshold || 0.1);
    this.stickyHideAt = Math.max(0.03, this.stickyThreshold - 0.03);
    this.stickyVisible = false;
    /** @type {'idle' | 'in' | 'out'} */
    this.stickyPhase = 'idle';
    this.ticking = false;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    /** @type {(() => void) | null} */
    this._cancelStickyAnim = null;

    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onScroll = this.onScroll.bind(this);

    this.bind();
    this.updateHeaderVars();
    this.syncSticky(true);
  }

  bind() {
    this.openBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.openDrawer());
    });
    this.closeBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.closeDrawer());
    });
    this.overlay?.addEventListener('click', () => this.closeDrawer());

    this.root.querySelectorAll('[data-ct-submenu-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-ct-submenu]');
        if (!item) return;
        const open = item.getAttribute('data-open') === 'true';
        item.setAttribute('data-open', open ? 'false' : 'true');
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });

    window.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize, { passive: true });

    if (this.stickyEnabled && this.rail) {
      this.bindScrollTarget();

      this._onSqueeze = () => {
        this.bindScrollTarget();
        this.syncSticky(true);
      };
      scrollContainerMediaQuery.addEventListener('change', this._onSqueeze);

      document.querySelectorAll('body > [data-ct-sticky-rail]').forEach((el) => {
        if (el !== this.rail) el.remove();
      });
      if (this.rail.parentElement !== document.body) {
        document.body.appendChild(this.rail);
      }
      this.rail.dataset.railBound = 'true';
    }

    // Portal drawer to body so it stacks above the sticky rail
    if (this.drawer && this.drawer.parentElement !== document.body) {
      document.querySelectorAll(`body > [data-ct-drawer]`).forEach((el) => {
        if (el !== this.drawer && el.id === this.drawer.id) el.remove();
      });
      document.body.appendChild(this.drawer);
    }

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target?.contains?.(this.root)) this.destroy();
    });
  }

  openDrawer() {
    if (!this.drawer) return;
    this.focusReturn = document.activeElement;
    this.drawer.hidden = false;
    this.openBtns.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    // Sticky lives on body above the old header stacking context — hide it while menu is open
    if (this.rail) {
      this.rail.setAttribute('aria-hidden', 'true');
      this.rail.toggleAttribute('inert', true);
    }
    requestAnimationFrame(() => {
      this.drawer?.setAttribute('data-open', 'true');
      document.documentElement.classList.add('ct-drawer-open');
      this.panel?.querySelector('[data-ct-drawer-close], a, button')?.focus();
    });
  }

  closeDrawer() {
    if (!this.drawer || this.drawer.getAttribute('data-open') !== 'true') return;
    this.drawer.setAttribute('data-open', 'false');
    document.documentElement.classList.remove('ct-drawer-open');
    this.openBtns.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    const onEnd = () => {
      this.drawer.hidden = true;
      this.drawer?.removeEventListener('transitionend', onEnd);
      if (this.stickyVisible && this.rail) {
        this.rail.setAttribute('aria-hidden', 'false');
        this.rail.toggleAttribute('inert', false);
      }
      if (this.focusReturn instanceof HTMLElement) this.focusReturn.focus();
    };
    this.drawer.addEventListener('transitionend', onEnd);
    setTimeout(onEnd, 420);
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.closeDrawer();
  }

  onResize() {
    this.updateHeaderVars();
    if (window.matchMedia('(min-width: 1200px)').matches) this.closeDrawer();
    this.syncSticky(true);
  }

  bindScrollTarget() {
    this.scrollTarget?.removeEventListener('scroll', this.onScroll);
    this._lenisOff?.();
    this._lenisOff = null;

    this.scrollTarget = getScrollEventTarget();
    this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true });

    // Lenis drives scroll via RAF — native scroll events can skip frames on fast flicks.
    // Subscribe directly so sticky state tracks every animated frame.
    const lenis = window.__ctLenis;
    if (lenis && typeof lenis.on === 'function') {
      const onLenisScroll = () => this.onScroll();
      lenis.on('scroll', onLenisScroll);
      this._lenisOff = () => {
        if (typeof lenis.off === 'function') lenis.off('scroll', onLenisScroll);
      };
    }
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.syncSticky(false);
      this.ticking = false;
    });
  }

  updateHeaderVars() {
    const header = this.bar || this.root;
    const group = document.querySelector('#header-group');
    if (header) {
      document.body.style.setProperty('--header-height', `${header.offsetHeight}px`);
    }
    if (group) {
      document.body.style.setProperty('--header-group-height', `${group.offsetHeight}px`);
    }
  }

  /**
   * Resolve scroll position from the same source that moves the header in the DOM.
   * Prefer the container's live scrollTop so getBoundingClientRect stays in sync on fast flicks
   * (Lenis.scroll can briefly lead/lag the painted position).
   * @returns {number}
   */
  getStickyScrollTop() {
    const container = getScrollContainer();
    const domTop = container?.scrollTop;
    if (typeof domTop === 'number' && Number.isFinite(domTop)) return domTop;
    return getScrollTop();
  }

  /** @param {boolean} immediate */
  syncSticky(immediate) {
    if (!this.stickyEnabled || !this.rail) return;

    const container = getScrollContainer();
    const scrollTop = this.getStickyScrollTop();
    const maxScroll = Math.max(1, (container?.scrollHeight || 0) - (container?.clientHeight || 0));

    // Pixel thresholds — page-height % alone flickers when images/scale change scrollHeight mid-flick.
    const showAfterPx = Math.max(48, this.stickyThreshold * maxScroll);
    const hideAfterPx = Math.max(24, this.stickyHideAt * maxScroll);
    // Extra px hysteresis so height jitter during fast scroll can't flip state mid-morph.
    const hideFloorPx = Math.max(hideAfterPx - 120, showAfterPx * 0.45);

    const barRect = this.bar?.getBoundingClientRect();
    const headerGone = barRect ? barRect.bottom < 8 : scrollTop >= showAfterPx;

    let shouldShow = false;
    if (this.stickyVisible || this.stickyPhase === 'in') {
      // Stay sealed once shown unless user is clearly back near the top.
      // Don't require headerGone once deep — getBoundingClientRect can flicker on fast flicks.
      shouldShow = scrollTop >= hideFloorPx && (headerGone || scrollTop >= showAfterPx);
    } else if (this.stickyPhase === 'out') {
      // Finish exit; only reverse if we've scrolled deep again.
      shouldShow = scrollTop >= showAfterPx && headerGone;
    } else {
      shouldShow = scrollTop >= showAfterPx && headerGone;
    }

    // Never interrupt the dew→seal entrance unless the user is back at the top.
    if (this.stickyPhase === 'in' && !shouldShow && scrollTop > hideFloorPx) {
      return;
    }

    // Already in the desired settled/animating direction — don't restart morphs.
    if (shouldShow === this.stickyVisible) return;

    // Don't claim visible while the drawer owns the screen
    if (shouldShow && document.documentElement.classList.contains('ct-drawer-open')) {
      return;
    }

    this.stickyVisible = shouldShow;
    this.setStickyVisible(shouldShow, immediate);
  }

  /**
   * @param {boolean} show
   * @param {boolean} immediate
   */
  setStickyVisible(show, immediate) {
    const rail = this.rail;
    if (!rail) return;

    // Cancel any in-flight enter/leave cleanup so fast scroll can't stack timeouts/listeners
    this._cancelStickyAnim?.();
    this._cancelStickyAnim = null;

    document.documentElement.classList.toggle('ct-sticky-rail-on', show);
    rail.setAttribute('aria-hidden', show ? 'false' : 'true');
    rail.toggleAttribute('inert', !show);

    // Drop animation classes first so the next morph always restarts cleanly
    rail.classList.remove('is-entering', 'is-leaving');

    if (this.reduced.matches || immediate) {
      rail.classList.toggle('is-visible', show);
      this.stickyPhase = 'idle';
      return;
    }

    // Force style recalc so re-adding animation classes restarts keyframes
    // eslint-disable-next-line no-unused-expressions
    void rail.offsetWidth;

    if (show) {
      this.stickyPhase = 'in';
      rail.classList.add('is-visible', 'is-entering');

      const clear = (event) => {
        if (event && event.target !== rail) return;
        if (event && event.animationName && !String(event.animationName).includes('ct-rail-dew-in')) {
          return;
        }
        finishEnter();
      };

      const finishEnter = () => {
        this._cancelStickyAnim = null;
        rail.removeEventListener('animationend', clear);
        rail.classList.remove('is-entering');
        if (this.stickyPhase === 'in') this.stickyPhase = 'idle';
      };

      rail.addEventListener('animationend', clear);
      const timer = window.setTimeout(finishEnter, 1850);
      this._cancelStickyAnim = () => {
        window.clearTimeout(timer);
        rail.removeEventListener('animationend', clear);
      };
    } else {
      this.stickyPhase = 'out';
      // Keep is-visible during leave so the exit morph can paint
      rail.classList.add('is-visible', 'is-leaving');

      const finish = (event) => {
        if (event && event.target !== rail) return;
        if (event && event.animationName && !String(event.animationName).includes('ct-rail-dew-out')) {
          return;
        }
        finishLeave();
      };

      const finishLeave = () => {
        this._cancelStickyAnim = null;
        rail.removeEventListener('animationend', finish);
        rail.classList.remove('is-visible', 'is-leaving', 'is-entering');
        if (this.stickyPhase === 'out') this.stickyPhase = 'idle';
      };

      rail.addEventListener('animationend', finish);
      const timer = window.setTimeout(finishLeave, 1350);
      this._cancelStickyAnim = () => {
        window.clearTimeout(timer);
        rail.removeEventListener('animationend', finish);
      };
    }
  }

  destroy() {
    window.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    this.scrollTarget?.removeEventListener('scroll', this.onScroll);
    this._lenisOff?.();
    this._lenisOff = null;
    this._cancelStickyAnim?.();
    this._cancelStickyAnim = null;
    if (this._onSqueeze) {
      scrollContainerMediaQuery.removeEventListener('change', this._onSqueeze);
    }
    document.documentElement.classList.remove('ct-drawer-open', 'ct-sticky-rail-on');
    if (this.rail?.parentElement === document.body) {
      this.rail.remove();
    }
    if (this.drawer?.parentElement === document.body) {
      this.drawer.remove();
    }
  }
}

const init = () => {
  document.querySelectorAll(SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement && !el.dataset.ctReady) {
      el.dataset.ctReady = 'true';
      new CtHeader(el);
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (!(section instanceof HTMLElement)) {
    init();
    return;
  }
  const root = section.matches(SELECTOR) ? section : section.querySelector(SELECTOR);
  if (root instanceof HTMLElement) {
    delete root.dataset.ctReady;
    init();
  }
});
